/**
 * Apply-state strip shared by the Price Rules and Condition Discounts tabs.
 * Rules are edited as a draft; this strip shows whether the current draft has
 * been applied to the catalog and offers the one primary action (Apply).
 */
import { useCallback, useState } from 'react';
import { useListContext, useNotify, useRefresh } from 'react-admin';
import { Box, Button, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import { requirePartner } from '@src/providers/dataProvider/shared';

import { initiatePricing } from '../pricingRules/initiatePricing';
import type { PricingType } from '../pricingRules/types';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

interface ApplyStatusStripProps {
  pricingType: PricingType;
}

const ApplyStatusStrip = ({ pricingType }: ApplyStatusStripProps) => {
  const { meta, isPending } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isApplying, setIsApplying] = useState(false);

  const initiatedAt = meta?.initiatedAt as string | null | undefined;
  const processedAt = meta?.processedAt as string | null | undefined;

  const handleApply = useCallback(async () => {
    setIsApplying(true);
    try {
      const partner = requirePartner();
      await initiatePricing({ partnerId: partner.id, pricingType });
      notify('Pricing rules are being applied', { type: 'success' });
      refresh();
    } catch (error) {
      console.error('ApplyStatusStrip: handleApply failed', {
        pricingType,
        error: error instanceof Error ? error.message : error,
      });
      notify(error instanceof Error ? error.message : 'Failed to apply pricing rules', { type: 'error' });
    } finally {
      setIsApplying(false);
    }
  }, [notify, pricingType, refresh]);

  if (isPending) return null;

  const isProcessing = Boolean(initiatedAt && !processedAt);
  const isProcessed = Boolean(initiatedAt && processedAt);

  const DOT_COLOR = { success: 'success.main', warning: 'warning.main', neutral: 'grey.400' } as const;
  let tone: keyof typeof DOT_COLOR = 'warning';
  let message = 'Unapplied changes — rules below are not live until you apply them to the catalog.';
  if (isProcessing) {
    tone = 'neutral';
    message = `Applying to catalog… started ${formatDate(initiatedAt)}. Prices update when processing completes.`;
  } else if (isProcessed) {
    tone = 'success';
    message = `Applied to catalog ${formatDate(processedAt)}. New edits will need another apply.`;
  }

  const applyBlockedReason = isProcessing ?
    'An apply is already in progress' :
    isProcessed ?
      'The current rules are already applied' :
      '';

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 2,
        py: 1.25,
        mx: 1.5,
        mt: 1.5,
        mb: 0.5,
        bgcolor: 'grey.50',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {isProcessing ?
          <CircularProgress size={12} thickness={5} sx={{ color: 'text.secondary', flexShrink: 0 }} /> :
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DOT_COLOR[tone], flexShrink: 0 }} />}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {message}
        </Typography>
      </Stack>
      <Tooltip title={applyBlockedReason}>
        <span>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={isApplying || Boolean(initiatedAt)}
            startIcon={isApplying ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {isApplying ? 'Applying…' : 'Apply to catalog'}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default ApplyStatusStrip;
